import React, { useEffect, useState } from 'react';
import { Grid, Typography, Button, Box, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, Popper, Card, CardContent } from '@mui/material';
import dayjs from 'dayjs';
import { getCalendarMonthEvents } from '../../../api/s10DocumentsApi';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { getEmployeesAdditionalDataById } from '../../../api/signalApi';
import { APPROVAL_EMPLOYEES } from '../../../constants/signalRequestConsts';
import { useLocation } from 'react-router-dom';

dayjs.locale('es');

interface CalendarProps {
  setCurrentDate: (date: dayjs.Dayjs) => void,
  currentDate: dayjs.Dayjs,
  isLoading: boolean;
  calendarData: any[];
}

const Calendar: React.FC<CalendarProps> = ({ setCurrentDate, currentDate, calendarData, isLoading }) => {
    const { employees } = useSelector((state: RootState) => state.userRequests).approvalGroups;
    const daysInMonth = currentDate.daysInMonth();
    const firstDayOfMonth = currentDate.startOf('month');
  
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedEvent, setSelectedEvent] = useState<any>(null);
    const [finalData, setFinalData] = useState<any>([]);
  
    const handlePrevMonth = () => {
      setCurrentDate(currentDate.subtract(1, 'month'));
    };
  
    const handleNextMonth = () => {
      setCurrentDate(currentDate.add(1, 'month'));
    };
  
    const handleCellClick = (event: any, element: HTMLElement) => {
        const isOpen = selectedEvent?.EmployeeCode === event?.EmployeeCode && selectedEvent?.selectedDate === event?.selectedDate;
        setAnchorEl(isOpen ? null : element
        );
        setSelectedEvent(isOpen ? null : event);
    };
  
    const getEventForUserOnDay = (data: any, day: Date) => {
      const dataDetails = data[`Day${day.getDate()}`];
      if (!dataDetails) return;
      const splitedData = dataDetails.split("|");
      return {
        id: splitedData[0],
        backgroundColor: splitedData[1],
        borderColor: splitedData[2],
        description: splitedData[3],
        status: splitedData[4],
        range: splitedData[5],
      };
    };

    function mergeCalendarAndEmployees(calendarData: any[], employees: any[]) {
      const finalData: any[] = [];
      
      const employeeMap = employees.reduce((map, emp) => {
        map[emp.CodIdentificador] = emp;
        return map;
      }, {});
    
      const companyMap: any = {};
      for (const calendar of calendarData) {
        const employee = employeeMap[calendar.EmployeeCode];
        if (!employee) continue;
        
        const mergedData = { ...calendar, ...employee };
        const ruc = employee.RUCEmpresa;
        const companyName = employee.DescripcionEmpresa;
        
        if (companyMap[ruc] === undefined) {
          companyMap[ruc] = finalData.length;
          finalData.push({
            ruc,
            companyName,
            employees: [mergedData]
          });
        } else {
          finalData[companyMap[ruc]].employees.push(mergedData);
        }
      }
    
      return finalData;
    }

    useEffect(() => {
      if (!calendarData.length || !employees.length) {
        return;
      }
      let finalData = mergeCalendarAndEmployees(calendarData, employees);
      setFinalData(finalData);
    }, [calendarData, employees])
  
    const isOpen = Boolean(anchorEl);
    const popperId = isOpen ? 'simple-popper' : undefined;

  
    return (
      <Box width="100%">
        <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
          <Button onClick={handlePrevMonth} disabled={isLoading}>Anterior</Button>
          <Typography variant="h6" sx={{ mx: 2 }}>
            {currentDate.format('MMMM YYYY')}
          </Typography>
          <Button onClick={handleNextMonth} disabled={isLoading}>Siguiente</Button>
        </Box>
  
        <TableContainer component={Paper} sx={{ overflowX: 'auto', maxWidth: '100%' }}>
          <Table sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '12em', position: 'sticky', left: 0, backgroundColor: '#fff', zIndex: 1, padding: ".5rem" }}>
                  Colaboradores
                </TableCell>
  
                {Array.from({ length: daysInMonth }, (_, index) => {
                  const date = firstDayOfMonth.add(index, 'day');
                  return (
                    <TableCell key={index} sx={{ textAlign: 'center', width: '3em', padding: 0 }}>
                      <Typography variant="body1">{date.date()}</Typography>
                      <Typography variant="body2">{date.format('ddd')}</Typography>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            {!isLoading && !!calendarData?.length && (
              <TableBody>
                {finalData.map((company: any) => (
                  <React.Fragment key={company.ruc}>
                  <TableRow>
                    <TableCell
                      sx={{
                        width: '12em',
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        padding: ".5rem",
                        backgroundColor: 'lightgray',
                      }}
                    >
                      {company.companyName}
                    </TableCell>
                    <TableCell
                      colSpan={daysInMonth}
                      sx={{backgroundColor: 'lightgray',}}
                    />
                  </TableRow>
                  {company.employees.map((employee: any) => (
                    <TableRow key={`${company.RUCEmpresa}-${employee.EmployeeCode}`}>
                      <TableCell
                        sx={{
                          width: '12em',
                          position: 'sticky',
                          left: 0,
                          backgroundColor: '#fff',
                          zIndex: 1,
                          whiteSpace: 'normal',
                          wordWrap: 'break-word',
                          padding: ".5rem",
                        }}
                      >
                        {employee.Descripcion}
                      </TableCell>
    
                      {Array.from({ length: daysInMonth }, (_, index) => {
                        const date = firstDayOfMonth.add(index, 'day');
                        const event = getEventForUserOnDay(employee, date.toDate());
    
                        return (
                          <TableCell key={index} sx={{ textAlign: 'center', padding: 0 }}>
                            {event ? (
                              <Grid
                                  style={{
                                      height: "3em",
                                      width: "3em",
                                      backgroundColor: event.backgroundColor,
                                      border: `3px solid ${event.borderColor || event.backgroundColor}`,
                                  }}
                                  onClick={(e) => handleCellClick({
                                          selectedDate: date.date(), 
                                          ...event, 
                                          ...employee
                                      }, 
                                      e.currentTarget
                                  )}
                              />
                            ) : <></>}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                  </React.Fragment>
                ))}
                
              </TableBody>
            )}
          </Table>
        </TableContainer>
  
        <Popper id={popperId} open={isOpen} anchorEl={anchorEl} placement="bottom" style={{zIndex: 100,}}>
          {selectedEvent && (
            <Card sx={{ minWidth: 275, mt: 1, p: 2 }} style={{zIndex: 100,}}>
              <CardContent>
                <Typography variant="h6">{selectedEvent.description}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Estado: {selectedEvent.status}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedEvent.range}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Popper>
  
        {isLoading && <Typography>Cargando...</Typography>}
      </Box>
    );
  };

const Index = () => {

  const location = useLocation();

  const {companyselected, connectionId} = useSelector((state: RootState) => state.auth);
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [calendarData, setCalendarData] = useState<any[]>([]);

  useEffect(() => {
      if (!companyselected || !connectionId) {
          return;
      }
      handleGetCalendarData(currentDate);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyselected, connectionId])

    const handleGetCalendarData = async(date: dayjs.Dayjs) => {
        setIsLoading(true);
        setCurrentDate(date);
        const {data} = await getCalendarMonthEvents(
          date.year(), 
          (date.month() + 1), 
          location.pathname.includes('a-mi-cargo'),
        );
        const employeesCodes = data ? data.map((e: any) => e.EmployeeCode) : [];
        console.log(data)
        await getEmployeesAdditionalDataById(APPROVAL_EMPLOYEES, employeesCodes);
        setCalendarData(data)
        setIsLoading(false);
    }
    
    return (
        // <Grid  container width="100%" >
            <Calendar
                // users={users}
                // events={events}
                calendarData={calendarData}
                currentDate={currentDate}
                setCurrentDate={handleGetCalendarData}
                isLoading={isLoading}
            />
        // </Grid>
    )

}

export default Index